import { buildSnapshotPayloadProxyUrl, isPrivateSnapshotPayloadPath } from '../../lib/snapshotPayloadProxy';

export type SnapshotRunKind = 'provisional' | 'final';
export type SnapshotFailureStage = 'meta' | 'ref' | 'payload';

type SnapshotRef = {
  snapshot_path?: string;
};

type SnapshotResponseOptions = {
  noStore?: boolean;
  retryOnForbidden?: boolean;
  mintEntitlements?: () => Promise<boolean>;
  cacheBustToken?: string | null;
  authenticatedFetch?: (
    url: string,
    options?: RequestInit & { includeAccessTokenInBody?: boolean }
  ) => Promise<Response>;
};

type SnapshotPayloadPathOptions = SnapshotResponseOptions & {
  refPath?: string | null;
  fallbackPath?: string | null;
};

type SnapshotRequestFailureOptions = {
  stage: SnapshotFailureStage;
  message: string;
  statusCode?: number | null;
  path?: string | null;
  cause?: unknown;
};

export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const MIN_DASHBOARD_DATE = '2024-07-01';
export const DASHBOARD_FREE_DELAY_DAYS = 30;
export const SNAPSHOTS_BASE_URL = (
  process.env.REACT_APP_SNAPSHOTS_BASE_URL || 'https://snapshots.quantchecklabs.com'
).replace(/\/+$/, '');
export const SNAPSHOT_META_PATH = '/v1/meta.json';

export const isIsoDate = (value: string | null): value is string => !!value && ISO_DATE_PATTERN.test(value);

export const isSnapshotRunKind = (value: string | null | undefined): value is SnapshotRunKind =>
  value === 'provisional' || value === 'final';

export const getUtcCutoffDate = (lagDays = DASHBOARD_FREE_DELAY_DAYS): string => {
  const now = new Date();
  const utcStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  utcStart.setUTCDate(utcStart.getUTCDate() - lagDays);
  return utcStart.toISOString().slice(0, 10);
};

export const normalizeSnapshotPath = (value: unknown): string | null =>
  typeof value === 'string' && value.startsWith('/') ? value : null;

export class SnapshotRequestFailure extends Error {
  readonly stage: SnapshotFailureStage;
  readonly statusCode: number | null;
  readonly path: string | null;

  constructor({ stage, message, statusCode, path, cause }: SnapshotRequestFailureOptions) {
    super(message);
    this.name = 'SnapshotRequestFailure';
    this.stage = stage;
    this.statusCode = typeof statusCode === 'number' ? statusCode : null;
    this.path = path || null;

    if (cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = cause;
    }
  }
}

export const createSnapshotRequestFailure = (
  options: SnapshotRequestFailureOptions
): SnapshotRequestFailure => new SnapshotRequestFailure(options);

export const readSnapshotRequestFailure = (
  error: unknown
): {
  stage: SnapshotFailureStage | null;
  statusCode: number | null;
  path: string | null;
} => {
  if (error instanceof SnapshotRequestFailure) {
    return {
      stage: error.stage,
      statusCode: error.statusCode,
      path: error.path,
    };
  }

  return {
    stage: null,
    statusCode: null,
    path: null,
  };
};

export const resolveSnapshotTemplatePath = (
  template: string,
  values: Record<string, string>
): string => {
  let next = template;
  for (const [key, value] of Object.entries(values)) {
    next = next.replaceAll(`{${key}}`, value);
  }
  return next;
};

const appendCacheBustToken = (path: string, cacheBustToken?: string | null): string => {
  if (!cacheBustToken) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}_ts=${encodeURIComponent(cacheBustToken)}`;
};

export const fetchSnapshotResponse = async (
  path: string,
  {
    noStore = false,
    retryOnForbidden = false,
    mintEntitlements,
    cacheBustToken,
    authenticatedFetch,
  }: SnapshotResponseOptions = {}
): Promise<Response> => {
  if (isPrivateSnapshotPayloadPath(path)) {
    const proxyUrl = buildSnapshotPayloadProxyUrl(path, cacheBustToken);
    if (authenticatedFetch) {
      return authenticatedFetch(proxyUrl, {
        cache: 'no-store',
        includeAccessTokenInBody: false,
      });
    }
    return fetch(proxyUrl, {
      credentials: 'include',
      cache: 'no-store',
    });
  }

  const requestPath = appendCacheBustToken(path, cacheBustToken);
  const request = () =>
    fetch(`${SNAPSHOTS_BASE_URL}${requestPath}`, {
      credentials: 'include',
      ...(noStore || cacheBustToken ? { cache: 'no-store' as const } : {}),
    });

  let response = await request();
  if (!response.ok && response.status === 403 && retryOnForbidden && mintEntitlements) {
    const minted = await mintEntitlements();
    if (minted) {
      response = await request();
    }
  }

  return response;
};

export const resolveSnapshotPayloadPath = async ({
  refPath,
  fallbackPath,
  noStore = true,
  retryOnForbidden = false,
  mintEntitlements,
  cacheBustToken,
  authenticatedFetch,
}: SnapshotPayloadPathOptions): Promise<string> => {
  if (!refPath) {
    if (fallbackPath) {
      return fallbackPath;
    }
    throw createSnapshotRequestFailure({
      stage: 'ref',
      message: 'Snapshot ref path is missing',
    });
  }

  try {
    const response = await fetchSnapshotResponse(refPath, {
      noStore,
      retryOnForbidden,
      mintEntitlements,
      cacheBustToken,
      authenticatedFetch,
    });
    if (!response.ok) {
      if (fallbackPath) {
        return fallbackPath;
      }
      throw createSnapshotRequestFailure({
        stage: 'ref',
        message: `Snapshot ref request failed with status ${response.status}`,
        statusCode: response.status,
        path: refPath,
      });
    }
    const payload = (await response.json()) as SnapshotRef;
    const snapshotPath = normalizeSnapshotPath(payload?.snapshot_path);
    if (snapshotPath) {
      return snapshotPath;
    }
    if (fallbackPath) {
      return fallbackPath;
    }
    throw createSnapshotRequestFailure({
      stage: 'ref',
      message: 'Snapshot ref response did not include a valid payload path',
      path: refPath,
    });
  } catch (error) {
    if (fallbackPath) {
      return fallbackPath;
    }
    if (error instanceof SnapshotRequestFailure) {
      throw error;
    }
    throw createSnapshotRequestFailure({
      stage: 'ref',
      message: error instanceof Error ? error.message : 'Failed to resolve snapshot payload path',
      path: refPath,
      cause: error,
    });
  }
};
